import { Machine, assign } from 'xstate';
import _orderBy from 'lodash/orderBy';
import _cloneDeep from 'lodash/cloneDeep';

import { logDetails } from '@trqk/utils';

import { initialContext } from './constants';

/**
 * State machine for managing the ManageGapAnalysis modal.
 *
 * @remarks
 * This state machine is built around a `steps` object whose keys are stepIds and values are
 * objects that describe each step.
 *
 * The steps (labeled by stepId) are:
 *
 * - `selectPeriod`: select a performance period
 * - `setUp`: display details for the performance and detection data that will be used to create the
 *   report. Allows the user to upload a statement by navigating to the Manage Statements modal. The
 *   user can use Trqk's internal detection data or upload detections from an external source. Also
 *   allows the user to upload detections by navigating to the `uploadDetections` step.
 * - `uploadDetections`: upload detections from an external source (i.e. BMAT)
 * - `analyzingData`: a loading screen that informs the user about next steps (`confirmChannels` and
 * `confirmWorks`)
 * - `confirmChannels`: select channels to include in the report. To select a channel, at least one
 * of its underlying audio files must be included.
 * - `confirmWorks`: select audio files to include for a specific channel in the report
 *
 * Each step has a `nextStepId` and `previousStepId`, which are used in conjunction with the
 * StepWrapper component to provide navigation links. Creating a Gap Analysis is not a linear
 * process, so some steps have `nextStepId: null`. An example is the `uploadDetections` step, where
 * the user uploads detections and then is taken back to the `setUp` step. This is similar to how
 * users navigate in a browser, so `context.stepHistory` works like a very simplified version of the
 * `history` object in React Router.
 *
 * When updating data in the ConfirmChannelsTable and ConfirmWorksTable, the edited state is kept
 * in this state machine's `context.edits` property. For example, to include a channel in the Gap
 * Analysis report, the IncludeChannelCell sends a TOGGLE_GAP_CHANNEL_CHECKED event that updates the
 * state of `context.edits[gapChannelId].checked`. Similarly, to a include an audio
 * file in the Gap Analysis report, the IncludeWorkCell sends a TOGGLE_GAP_AUDIO_CHECKED event that
 * updates the state of `context.edits[gapChannelId].checkedAudioIds`.
 */
export const gapAnalysisMachine = Machine<GapAnalysisContext, GapAnalysisEvent>(
    {
        type: 'compound',
        initial: 'running',
        // Need to use _cloneDeep here to avoid mutating properties of nested objects
        context: _cloneDeep(initialContext),
        states: {
            running: {
                on: {
                    GO_TO_NEXT_STEP: {
                        actions: ['goToNextStep'],
                    },
                    GO_TO_PREVIOUS_STEP: {
                        actions: ['goToPreviousStep'],
                    },
                    GO_TO_STEP: {
                        actions: ['goToStep'],
                    },
                    SET_STEP_HISTORY: {
                        actions: ['setStepHistory'],
                    },
                    REPLACE_CURRENT_STEP: {
                        actions: ['replaceCurrentStep'],
                    },
                    SET_SELECTED_PERIOD: {
                        actions: ['setSelectedPeriod'],
                    },
                    UPDATE_SELECTED_PERIOD: {
                        actions: ['updateSelectedPeriod'],
                    },
                    SET_PERIOD_SELECTIONS: {
                        actions: ['setPeriodSelections'],
                    },
                    UPDATE_STEP: {
                        actions: ['updateStep'],
                    },
                    SET_TRQK_GAP: {
                        actions: ['setTrqkGap'],
                    },
                    UPDATE_AUDIO_LISTS: {
                        actions: ['updateAudioLists'],
                    },
                    SET_CHANNELS: {
                        actions: ['setChannels'],
                    },
                    UPDATE_CHANNEL_STATUS: {
                        actions: ['updateChannelStatus'],
                    },
                    TOGGLE_GAP_CHANNEL_CHECKED: {
                        actions: ['toggleGapChannelChecked'],
                    },
                    TOGGLE_ALL_GAP_CHANNELS_CHECKED: {
                        actions: ['toggleAllGapChannelsChecked'],
                    },
                    TOGGLE_ALL_GAP_AUDIOS_CHECKED: {
                        actions: ['toggleAllGapAudiosChecked'],
                    },
                    START_CONFIRMING_WORKS: {
                        actions: ['startConfirmingWorks'],
                    },
                    EDIT_AUDIO_MATCHED_WORK_ID: {
                        actions: ['editAudioMatchedWorkId'],
                    },
                    EDIT_AUDIO_INCLUDE: {
                        actions: ['editAudioInclude'],
                    },
                    CLEAR_AUDIO_EDITS: {
                        actions: ['clearAudioEdits'],
                    },
                    REPLACE_CONTEXT: {
                        actions: ['replaceContext'],
                    },
                    RESTORE_CONTEXT: {
                        actions: ['restoreContext'],
                    },
                    ADD_LOG_DETAIL: {
                        actions: ['addLogDetail'],
                    },
                    CLEAR_LOG_DETAILS: {
                        actions: ['clearLogDetails'],
                    },
                    SET_LOADING: {
                        actions: ['setLoading'],
                    },
                    SET_MODE: {
                        actions: ['setMode'],
                    },
                    SET_REPORT_DESCRIPTION: {
                        actions: ['setReportDescription'],
                    },
                    SET_FILTER_OPTIONS: {
                        actions: ['setFilterOptions'],
                    },
                    SET_UPLOADED_DETECTION_FILE: {
                        actions: ['setUploadedDetectionFile'],
                    },
                },
            },
        },
    },
    {
        actions: {
            logDetails,
            /**
             * Go to the next step in the Create Gap Analysis workflow. This pushes a stepId onto
             * `context.stepHistory`, which means that Breadcrumbs will show an additional step has
             * been added.
             */
            goToNextStep: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'GO_TO_NEXT_STEP') {
                        return context;
                    }

                    const {
                        value: { nextStepId },
                    } = event;

                    const existingIndex =
                        context.stepHistory.indexOf(nextStepId);

                    // If we are moving to a step that is already in `context.stepHistory`, use the
                    // existing history up until that point
                    if (existingIndex > -1) {
                        return {
                            ...context,
                            stepHistory: context.stepHistory.slice(
                                0,
                                existingIndex + 1,
                            ),
                        };
                    }

                    return {
                        ...context,
                        stepHistory: [...context.stepHistory, nextStepId],
                    };
                },
            ),
            /**
             * Go back to the previous step. This pops a stepId off of `context.stepHistory`, which
             * means that Breadcrumbs will show a step has been removed.
             * When event.value.resetPreviousStepTitle is true, the previous `step.title` will be
             * set to `null`, which means that Breadcrumbs will show the `step.defaultTitle` instead.
             */
            goToPreviousStep: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'GO_TO_PREVIOUS_STEP') {
                        return context;
                    }
                    const nextStepHistory = [...context.stepHistory];
                    const {
                        value: { resetPreviousStepTitle },
                    } = event;

                    nextStepHistory.pop();

                    const previousStepId =
                        nextStepHistory?.[nextStepHistory.length - 1] ?? null;

                    if (!resetPreviousStepTitle || previousStepId === null) {
                        return {
                            ...context,
                            stepHistory: nextStepHistory,
                        };
                    }

                    const nextSteps = _cloneDeep(context.steps);

                    nextSteps[previousStepId].title = null;

                    return {
                        ...context,
                        stepHistory: nextStepHistory,
                        steps: nextSteps,
                    };
                },
            ),
            /**
             * Go to a step specified by `event.value.stepId`. This `stepId` will be pushed onto
             * `context.stepHistory`.
             */
            goToStep: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'GO_TO_STEP') {
                        return context;
                    }
                    const {
                        value: { stepId },
                    } = event;

                    const nextStepHistory = [...context.stepHistory, stepId];

                    return {
                        ...context,
                        stepHistory: nextStepHistory,
                    };
                },
            ),
            /**
             * Overwrite `context.stepHistory` with a new value.
             */
            setStepHistory: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_STEP_HISTORY') {
                        return context;
                    }
                    const {
                        value: { stepHistory },
                    } = event;

                    return {
                        ...context,
                        stepHistory,
                    };
                },
            ),
            /**
             * Replace the current stepId in `context.stepHistory` with a different stepId. This
             * means the last label in Breadcrumbs will be reset to the label for the step specified
             * by `event.value.newCurrentStepId`.
             */
            replaceCurrentStep: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'REPLACE_CURRENT_STEP') {
                        return context;
                    }

                    const {
                        value: { newCurrentStepId },
                    } = event;
                    const nextStepHistory = [...context.stepHistory];

                    nextStepHistory.pop();
                    nextStepHistory.push(newCurrentStepId);

                    return {
                        ...context,
                        stepHistory: nextStepHistory,
                    };
                },
            ),
            /**
             * Set the value of `context.selectedPeriod`.
             */
            setSelectedPeriod: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_SELECTED_PERIOD') {
                        return context;
                    }

                    const {
                        value: { selectedPeriod },
                    } = event;

                    return {
                        ...context,
                        selectedPeriod,
                    };
                },
            ),
            /**
             * Merge an update with `context.selectedPeriod`. Also, find the matching period in
             * `context.periodSelections` and update it as well.
             */
            updateSelectedPeriod: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'UPDATE_SELECTED_PERIOD') {
                        return context;
                    }

                    const {
                        value: { update },
                    } = event;
                    const { selectedPeriod, periodSelections } = context;

                    if (selectedPeriod === null) {
                        return context;
                    }

                    const nextSelectedPeriod = { ...selectedPeriod, ...update };
                    const nextPeriodSelections = periodSelections.map(
                        (
                            currentPeriodSelection: GapAnalysisPeriodSelection,
                        ): GapAnalysisPeriodSelection => {
                            if (
                                currentPeriodSelection.performance_period_label ===
                                nextSelectedPeriod.performance_period_label
                            ) {
                                return nextSelectedPeriod;
                            }

                            return currentPeriodSelection;
                        },
                    );

                    return {
                        ...context,
                        selectedPeriod: nextSelectedPeriod,
                        periodSelections: nextPeriodSelections,
                    };
                },
            ),
            /**
             * Set the value of `context.periodSelections` to an array of period selections sorted
             * by their `order` property. If there is a `context.selectedPeriod` that matches one
             * of the `event.value.periodSelections`, update it from the matching period selection
             * in `event.value.periodSelections`.
             */
            setPeriodSelections: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_PERIOD_SELECTIONS') {
                        return context;
                    }

                    const {
                        value: { periodSelections },
                    } = event;
                    const { selectedPeriod } = context;
                    const periodSelectionsSorted = _orderBy(
                        periodSelections,
                        ['order'],
                        ['desc'],
                    );
                    const updatedSelectedPeriod = periodSelections.find(
                        ({
                            performance_period_label,
                        }: GapAnalysisPeriodSelection): boolean =>
                            performance_period_label ===
                            selectedPeriod?.performance_period_label,
                    );

                    if (updatedSelectedPeriod) {
                        return {
                            ...context,
                            periodSelections: periodSelectionsSorted,
                            selectedPeriod: updatedSelectedPeriod,
                        };
                    }

                    return {
                        ...context,
                        periodSelections: periodSelectionsSorted,
                    };
                },
            ),
            /**
             * Merge an update with a step in `context.steps`.
             */
            updateStep: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'UPDATE_STEP') {
                        return context;
                    }

                    const {
                        value: { stepId, update },
                    } = event;

                    const { steps } = context;

                    const targetStep = steps[stepId];

                    if (!targetStep) {
                        return context;
                    }

                    const nextSteps = {
                        ...steps,
                        [stepId]: {
                            ...targetStep,
                            ...update,
                        },
                    };

                    return {
                        ...context,
                        steps: nextSteps,
                    };
                },
            ),
            /**
             * Set the value of `context.trqkGap` and `context.statementId`.
             */
            setTrqkGap: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_TRQK_GAP') {
                        return context;
                    }

                    const {
                        value: { trqkGap },
                    } = event;

                    const {
                        statement_id_standard,
                        statement_id_international,
                    } = trqkGap;

                    return {
                        ...context,
                        statementId:
                            statement_id_standard ?? statement_id_international,
                        trqkGap,
                        trqkGapUpdated: Date.now(),
                    };
                },
            ),
            /**
             * Set the value of `context.channels` and set the initial state of `context.edits`.
             */
            setChannels: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_CHANNELS') {
                        return context;
                    }

                    const {
                        value: { channels },
                    } = event;

                    const initialEdits = channels.reduce(
                        (
                            acc: Record<string, GapAnalysisEdits>,
                            { include, channel_id }: TrqkGapChannel,
                        ) => ({
                            ...acc,
                            [channel_id]: {
                                audioEdits: {},
                                checked: include ?? false,
                                someMatchedWorkIdWasEdited: false,
                                someIncludeWasEdited: false,
                            },
                        }),
                        {},
                    );

                    return {
                        ...context,
                        edits: initialEdits,
                        channels,
                        channelsUpdated: Date.now(),
                    };
                },
            ),
            /**
             * Update the value of an individual `channel_status` within `context.channels`.
             */
            updateChannelStatus: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'UPDATE_CHANNEL_STATUS') {
                        return context;
                    }

                    const {
                        value: { channelId, status },
                    } = event;

                    const nextChannels = context.channels.map(channel => {
                        if (channel.channel_id === channelId) {
                            return {
                                ...channel,
                                channel_status: status,
                            };
                        }

                        return channel;
                    });

                    return {
                        ...context,
                        channels: nextChannels,
                        channelsUpdated: Date.now(),
                    };
                },
            ),
            /**
             * Given an array of {@link TrqkGapChannelAudio} objects and a `channelId`, update
             * audios within `context.audioLists[channelId]`.
             */
            updateAudioLists: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'UPDATE_AUDIO_LISTS') {
                        return context;
                    }

                    const {
                        value: { channelId, audios },
                    } = event;
                    const { audioLists, audiosUpdated } = context;

                    // Calculate initial state of `checkedAudioIds` from saved state of audio data;
                    // if an audio has `include === true`, mark it as checked
                    const initialCheckedAudioIds = audios.reduce(
                        (acc: string[], { gap_audio_id, include }) => {
                            if (include && gap_audio_id) {
                                acc.push(gap_audio_id);
                            }

                            return acc;
                        },
                        [],
                    );

                    return {
                        ...context,
                        audioLists: {
                            ...audioLists,
                            [channelId]: audios,
                        },
                        audiosUpdated: {
                            ...audiosUpdated,
                            [channelId]: Date.now(),
                        },
                        edits: {
                            ...context.edits,
                            [channelId]: {
                                ...context.edits[channelId],
                                checkedAudioIds: initialCheckedAudioIds,
                            },
                        },
                    };
                },
            ),
            /**
             * Update the state of `context.edits` to reflect whether or not a gap channel has been
             * selected by the user to be included in the report.
             */
            toggleGapChannelChecked: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'TOGGLE_GAP_CHANNEL_CHECKED') {
                        return context;
                    }

                    const {
                        value: { channelId, checked },
                    } = event;

                    const existingEdit = context.edits?.[channelId] ?? null;

                    if (!existingEdit) {
                        return {
                            ...context,
                            edits: {
                                ...context.edits,
                                [channelId]: {
                                    audioEdits: {},
                                    checkedAudioIds: [],
                                    checked,
                                    someMatchedWorkIdWasEdited: false,
                                    someIncludeWasEdited: false,
                                },
                            },
                            channelsUpdated: Date.now(),
                        };
                    }

                    const nextEdit = {
                        ...existingEdit,
                        checked,
                    };

                    return {
                        ...context,
                        edits: {
                            ...context.edits,
                            [channelId]: nextEdit,
                        },
                        channelsUpdated: Date.now(),
                    };
                },
            ),
            /**
             * Update the state of all `context.edits` to reflect whether or not each gap channel
             * has been selected by the user to be included in the report.
             */
            toggleAllGapChannelsChecked: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'TOGGLE_ALL_GAP_CHANNELS_CHECKED') {
                        return context;
                    }

                    const {
                        value: { checked },
                    } = event;

                    const nextEdits = {} as Record<string, GapAnalysisEdits>;

                    Object.entries(context.edits).forEach(
                        ([channelId, edit]) => {
                            nextEdits[channelId] = {
                                ...edit,
                                checked,
                            };
                        },
                    );

                    return {
                        ...context,
                        edits: nextEdits,
                        channelsUpdated: Date.now(),
                    };
                },
            ),
            /**
             * Update the state of the current channel's `audioEdits` to reflect whether or not each
             * gap audio has been selected by the user to be included in the report.
             */
            toggleAllGapAudiosChecked: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'TOGGLE_ALL_GAP_AUDIOS_CHECKED') {
                        return context;
                    }

                    const {
                        value: { channelId, checked },
                    } = event;
                    const nextEdits = { ...context.edits };

                    const channelEdits = nextEdits[channelId];

                    if (channelEdits) {
                        const audios = context.audioLists[channelId];
                        const existingAudioEdits = channelEdits.audioEdits;

                        if (audios) {
                            const nextAudioEdits = audios.reduce(
                                (acc, original: TrqkGapChannelAudio) => {
                                    const { gap_audio_id } = original;

                                    const existingAudioEdit =
                                        existingAudioEdits?.[gap_audio_id];

                                    const originalMatchedWorkId =
                                        original?.matched_work_id ?? null;

                                    if (existingAudioEdit) {
                                        acc[gap_audio_id] = {
                                            ...existingAudioEdit,
                                            edited: {
                                                ...existingAudioEdit.edited,
                                                include: checked,
                                            },
                                        };
                                    } else {
                                        acc[gap_audio_id] = {
                                            gapAudioId: gap_audio_id,
                                            original: {
                                                include:
                                                    original?.include ?? false,
                                                songId: originalMatchedWorkId,
                                            },
                                            edited: {
                                                include: checked,
                                            },
                                        };
                                    }

                                    return acc;
                                },
                                {} as Record<string, GapAudioEdit>,
                            );

                            nextEdits[channelId].audioEdits = nextAudioEdits;

                            const audioEditList = Object.values(nextAudioEdits);

                            nextEdits[channelId].someIncludeWasEdited =
                                audioEditList.some(
                                    audioEdit =>
                                        audioEdit.edited.include !==
                                            undefined &&
                                        audioEdit.edited.include !==
                                            audioEdit.original.include,
                                );
                        }
                    }

                    const { steps } = context;
                    const { confirmWorks } = steps;

                    const nextSteps = {
                        ...steps,
                        confirmWorks,
                    };

                    const enableApply =
                        nextEdits[channelId].someMatchedWorkIdWasEdited ||
                        nextEdits[channelId].someIncludeWasEdited;

                    // disableNextStep is a misnomer here; we're really enabling the
                    // "Apply" button if the user has changed some `matched_work_id` or some
                    // `include`. In most steps of the workflow, this is the button that takes
                    // user to the next step, but in this case, it just updates data and
                    // re-renders the table.
                    nextSteps.confirmWorks.disableNextStep = !enableApply;

                    return {
                        ...context,
                        edits: nextEdits,
                        steps: nextSteps,
                    };
                },
            ),
            /**
             * Move to the ConfirmWorks step and set the step title to the current channel
             * name. Also, set values as follows:
             * `context.loading: true`
             * `context.steps.confirmWorks.disablePreviousStep: true`.
             * `context.steps.confirmWorks.disableNextStep: true`.
             */
            startConfirmingWorks: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'START_CONFIRMING_WORKS') {
                        return context;
                    }

                    const {
                        value: { currentChannelId, channelName },
                    } = event;
                    // Need to use _cloneDeep here to avoid mutating properties of nested objects
                    const nextSteps = _cloneDeep(context.steps);

                    nextSteps.confirmChannels.title = channelName;
                    nextSteps.confirmWorks.disablePreviousStep = true;
                    nextSteps.confirmWorks.disableNextStep = true;

                    return {
                        ...context,
                        loading: true,
                        stepHistory: [...context.stepHistory, 'confirmWorks'],
                        steps: nextSteps,
                        currentChannelId,
                    };
                },
            ),
            /**
             * Add a pending audio edit for a detected audio file that will updated its
             * `matched_work_id`. This happens in ConfirmWorksTable when the user is changing the
             * `matched_work_id` via the Statement Work dropdown.
             */
            editAudioMatchedWorkId: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'EDIT_AUDIO_MATCHED_WORK_ID') {
                        return context;
                    }
                    const {
                        value: { gapAudioId, channelId, songId },
                    } = event;

                    const existingChannelEdits = context.edits?.[channelId];

                    let nextChannelEdits: GapAnalysisEdits = {
                        audioEdits: {},
                        checked: false,
                        someMatchedWorkIdWasEdited: false,
                        someIncludeWasEdited: false,
                    };

                    if (existingChannelEdits) {
                        nextChannelEdits = {
                            ...existingChannelEdits,
                        };
                    }

                    const nextAudioEdits = nextChannelEdits.audioEdits;

                    const originalAudio = context.audioLists?.[channelId]?.find(
                        (audio: TrqkGapChannelAudio) =>
                            audio.gap_audio_id === gapAudioId,
                    );

                    const original = {
                        include: originalAudio?.include ?? false,
                        songId: originalAudio?.matched_work_id ?? null,
                    };

                    const existingAudioEdit =
                        nextAudioEdits?.[gapAudioId] ?? null;

                    let nextAudioEdit: GapAudioEdit = {
                        gapAudioId,
                        original,
                        edited: {
                            songId,
                        },
                    };

                    if (existingAudioEdit) {
                        nextAudioEdit = {
                            ...existingAudioEdit,
                            edited: {
                                ...existingAudioEdit.edited,
                                songId,
                            },
                        };
                    }

                    const songIdIsOriginal =
                        songId === original.songId ||
                        nextAudioEdit.edited.songId === original.songId;

                    if (songIdIsOriginal) {
                        // If the `songId` is the same as the original `songId`, then there's no
                        // need to create an edit
                        delete nextAudioEdit.edited.songId;
                    }

                    nextAudioEdits[gapAudioId] = nextAudioEdit;

                    const audioEditList = Object.values(nextAudioEdits);

                    nextChannelEdits.someMatchedWorkIdWasEdited =
                        audioEditList.some(
                            audioEdit =>
                                audioEdit.edited.songId !== undefined &&
                                audioEdit.edited.songId !==
                                    audioEdit.original.songId,
                        );

                    const { steps } = context;
                    const { confirmWorks } = steps;

                    const nextSteps = {
                        ...steps,
                        confirmWorks,
                    };

                    const enableApply =
                        nextChannelEdits.someMatchedWorkIdWasEdited ||
                        nextChannelEdits.someIncludeWasEdited;

                    // disableNextStep is a misnomer here; we're really enabling the
                    // "Apply" button if the user has changed some `matched_work_id` or some
                    // `include`. In most steps of the workflow, this is the button that takes
                    // user to the next step, but in this case, it just updates data and
                    // re-renders the table.
                    nextSteps.confirmWorks.disableNextStep = !enableApply;

                    return {
                        ...context,
                        edits: {
                            ...context.edits,
                            [channelId]: nextChannelEdits,
                        },
                        steps: nextSteps,
                    };
                },
            ),
            /**
             * Add a pending audio edit for a detected audio file that will update its `include`
             * value. This happens in ConfirmWorksTable when the user is toggling the checkbox
             * with the IncludeWorkCell.
             */
            editAudioInclude: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'EDIT_AUDIO_INCLUDE') {
                        return context;
                    }
                    const {
                        value: { gapAudioId, channelId, include },
                    } = event;

                    const existingChannelEdits = context.edits?.[channelId];

                    let nextChannelEdits: GapAnalysisEdits = {
                        audioEdits: {},
                        checked: false,
                        someMatchedWorkIdWasEdited: false,
                        someIncludeWasEdited: false,
                    };

                    if (existingChannelEdits) {
                        nextChannelEdits = {
                            ...existingChannelEdits,
                        };
                    }

                    const nextAudioEdits = nextChannelEdits.audioEdits;

                    const originalAudio = context.audioLists?.[channelId]?.find(
                        (audio: TrqkGapChannelAudio) =>
                            audio.gap_audio_id === gapAudioId,
                    );

                    const original = {
                        include: originalAudio?.include ?? false,
                        songId: originalAudio?.matched_work_id ?? null,
                    };

                    const existingAudioEdit =
                        nextAudioEdits?.[gapAudioId] ?? null;

                    let nextAudioEdit: GapAudioEdit = {
                        gapAudioId,
                        original,
                        edited: {
                            include,
                        },
                    };

                    if (existingAudioEdit) {
                        nextAudioEdit = {
                            ...existingAudioEdit,
                            edited: {
                                ...existingAudioEdit.edited,
                                include,
                            },
                        };
                    }

                    const includeIsOriginal =
                        nextAudioEdit.edited.include === original.include;

                    if (includeIsOriginal) {
                        // If the `include` is the same as the original `include`, then there's no
                        // need to create an edit
                        delete nextAudioEdit.edited.include;
                    }

                    nextAudioEdits[gapAudioId] = nextAudioEdit;

                    const audioEditList = Object.values(nextAudioEdits);

                    nextChannelEdits.someIncludeWasEdited = audioEditList.some(
                        audioEdit =>
                            audioEdit.edited.include !== undefined &&
                            audioEdit.edited.include !==
                                audioEdit.original.include,
                    );

                    const { steps } = context;
                    const { confirmWorks } = steps;

                    const nextSteps = {
                        ...steps,
                        confirmWorks,
                    };

                    const enableApply =
                        nextChannelEdits.someMatchedWorkIdWasEdited ||
                        nextChannelEdits.someIncludeWasEdited;

                    // disableNextStep is a misnomer here; we're really enabling the
                    // "Apply" button if the user has changed some `matched_work_id` or some
                    // `include`. In most steps of the workflow, this is the button that takes
                    // user to the next step, but in this case, it just updates data and
                    // re-renders the table.
                    nextSteps.confirmWorks.disableNextStep = !enableApply;

                    return {
                        ...context,
                        edits: {
                            ...context.edits,
                            [channelId]: nextChannelEdits,
                        },
                        steps: nextSteps,
                    };
                },
            ),
            /**
             * Set the value of `context.edits[channelId].audioEdits to an empty object.
             */
            clearAudioEdits: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'CLEAR_AUDIO_EDITS') {
                        return context;
                    }

                    const {
                        value: { channelId },
                    } = event;

                    const existingChannelEdits = context.edits?.[channelId];

                    const checked = existingChannelEdits?.checked ?? false;

                    return {
                        ...context,
                        edits: {
                            ...context.edits,
                            [channelId]: {
                                checked,
                                audioEdits: {},
                                someMatchedWorkIdWasEdited: false,
                                someIncludeWasEdited: false,
                            },
                        },
                    };
                },
            ),
            /**
             * Replace the current context with `event.value.newContext`. This is used when
             * navigating back to the ManageGapAnalysisModal from the ManageStatementsModal. This
             * allows the user to pick up where they left off when they left the
             * ManageGapAnalysisModal.
             */
            replaceContext: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'REPLACE_CONTEXT') {
                        return context;
                    }

                    return event.value.newContext;
                },
            ),
            /**
             * Restore the initial context but preserve `context.periodSelections`.
             */
            restoreContext: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'RESTORE_CONTEXT') {
                        return context;
                    }

                    return {
                        ...initialContext,
                        periodSelections: context.periodSelections,
                    };
                },
            ),
            /**
             * Add a log event detail to `context.logDetails`, under the key designated by
             * `event.value.gapAnalysisId`.
             */
            addLogDetail: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'ADD_LOG_DETAIL') {
                        return context;
                    }
                    const {
                        value: { gapAnalysisId, logDetail },
                    } = event;

                    return {
                        ...context,
                        logDetails: {
                            ...context.logDetails,
                            [gapAnalysisId]: [
                                ...(context.logDetails?.[gapAnalysisId] ?? []),
                                logDetail,
                            ],
                        },
                    };
                },
            ),
            /**
             * Resets `context.logDetails[gapAnalysisId]` to an empty array.
             */
            clearLogDetails: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'CLEAR_LOG_DETAILS') {
                        return context;
                    }
                    const {
                        value: { gapAnalysisId },
                    } = event;

                    return {
                        ...context,
                        logDetails: {
                            ...context.logDetails,
                            [gapAnalysisId]: [],
                        },
                    };
                },
            ),
            /**
             * Set the next state of `context.loading`.
             */
            setLoading: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_LOADING') {
                        return context;
                    }

                    const {
                        value: { loading },
                    } = event;

                    return {
                        ...context,
                        loading,
                    };
                },
            ),
            /**
             * Set the next state of `context.mode`.
             */
            setMode: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_MODE') {
                        return context;
                    }

                    const {
                        value: { mode },
                    } = event;

                    return {
                        ...context,
                        mode,
                    };
                },
            ),
            /**
             * Set the next state of `context.reportDescription`.
             */
            setReportDescription: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_REPORT_DESCRIPTION') {
                        return context;
                    }

                    const {
                        value: { reportDescription },
                    } = event;

                    return {
                        ...context,
                        reportDescription,
                    };
                },
            ),
            /**
             * Set the next state of `context.filterOptions`.
             */
            setFilterOptions: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_FILTER_OPTIONS') {
                        return context;
                    }

                    const {
                        value: { filterOptions },
                    } = event;

                    return {
                        ...context,
                        filterOptions,
                    };
                },
            ),
            /**
             * Set the next state of `context.uploadedDetectionFile`.
             */
            setUploadedDetectionFile: assign(
                (context: GapAnalysisContext, event: GapAnalysisEvent) => {
                    if (event.type !== 'SET_UPLOADED_DETECTION_FILE') {
                        return context;
                    }

                    const {
                        value: { uploadedDetectionFile },
                    } = event;

                    return {
                        ...context,
                        uploadedDetectionFile,
                    };
                },
            ),
        },
    },
);
